import { colors } from 'fitify-ui'
import { TextStyles } from 'fitify-ui-landing/src/components/Text'
import { ThemeVariants } from 'fitify-ui-landing/src/theme'
import { biggerPhone, phone } from 'fitify-ui-landing/src/theme/breakpoints'
import styled, { css, keyframes } from 'styled-components'

interface StyledContactProps {
  isSuccess?: boolean
  hasError?: boolean
}

const spinner = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

export const StyledContact = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  padding: 32px;
  border-radius: 24px;
  background: ${colors.White};
  gap: 24px;

  ${biggerPhone(css`
    display: flex;
    flex-direction: column;
    padding: 24px;
    border-radius: 16px;
  `)};
`

export const StyledContactCopy = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;

  ${TextStyles} {
    b {
      color: ${colors.Black};
    }
  }

  h2 {
    margin-bottom: 32px;
  }

  ${biggerPhone(css`
    p {
      margin-bottom: 16px;
    }
  `)};
`

export const StyledContactForm = styled.form`
  position: relative;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  text-align: center;
`

export const StyledContactInput = styled.input<{ invalid?: boolean }>`
  display: block;
  width: 100%;
  min-height: 56px;
  padding: 0 24px;
  border: none;
  border-radius: 100px;
  margin-bottom: 8px;
  background-color: ${(props) =>
    props.invalid ? `${colors.Red50}` : `${colors.Gray200}`};
  color: ${colors.Black};
  font-family: 'Graphik LCG Web', sans-serif;
  font-size: 16px;
  font-weight: 500;
  outline-offset: 0;
  outline-style: none;
  outline-width: 3px;
  transition: background-color 0.2s ease-in-out;

  &:disabled {
    opacity: 1;
  }

  ${phone(css`
    font-size: 16px;
  `)};

  &::placeholder {
    color: ${colors.Black};
  }
`

export const StyledContactEmailSuccessMessage = styled.span<StyledContactProps>`
  display: ${(props) => (props.isSuccess ? 'flex' : 'none')};
  width: 100%;
  min-height: 56px;
  align-items: center;
  padding: 0 24px;
  border: none;
  border-radius: 100px;
  margin-top: 16px;
  background-color: ${colors.Green50};
  color: ${colors.Black};
  font-family: 'Graphik LCG Web', sans-serif;
  font-size: 20px;
  font-weight: 500;
  outline-offset: 0;
  outline-style: none;
  outline-width: 3px;
  text-align: start;

  ${phone(css`
    font-size: 16px;
    resize: vertical;
  `)};
`

export const ButtonLoader = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin: 20px auto;
  animation: ${spinner} 1.4s infinite linear;
  background: transparent;
  background: linear-gradient(
    to right,
    ${colors.Gray500} 10%,
    rgb(255 255 255 / 0%) 42%
  );
  cursor: default;
  font-size: 10px;
  inset: 0 20px auto auto;
  transform: translateZ(0);

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    width: 50%;
    height: 50%;
    border-radius: 100% 0 0;
    background: transparent;
    content: '';
  }

  &::after {
    position: absolute;
    width: 75%;
    height: 75%;
    border-radius: 50%;
    margin: auto;
    content: '';
    inset: 0;
  }
`

export const StyledContactButton = styled.button`
  position: relative;
  width: 100%;
  height: 56px;
  padding: 0 16px;
  border: 0;
  border-radius: 100px;
  margin-top: 12px;
  cursor: pointer;
  font-family: 'Graphik LCG Web', sans-serif;
  font-size: 20px;
  font-weight: 500;

  ${({ theme }) => {
    return css`
      background-color: ${theme.variant === ThemeVariants.DIGITAL
        ? colors.Blue400
        : colors.Green400};
      color: ${theme.variant === ThemeVariants.DIGITAL
        ? colors.White
        : colors.Black};
    `
  }}

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }

  ${biggerPhone(css`
    cursor: default;

    &:focus {
      outline: none;
    }
  `)}
`

export const StyledContactErrorMessage = styled.span`
  margin-bottom: 8px;
  color: ${colors.Red400};
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  text-transform: uppercase;
`
